import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { AuthShell } from "../components/AuthShell";
import { SeoForPage } from "../components/Seo";
import { useT, useLp, useLang } from "../lib/i18n";
import { trackDesktopDownloadClicked } from "../lib/launch-funnel";
import { currentSourcePage, trackDownloadClick } from "../lib/plausible";
import {
  downloadUrl,
  downloadUrlForBuild,
  detectMacArch,
  detectOS,
  type MacArch,
  type DetectedOS,
} from "../lib/api";

/**
 * /downloads — manual picker for the desktop build. We detect the visitor's
 * OS via user-agent hints and show the matching section first (Mac arm64/x64
 * or Windows installer/portable). The other section is still shown below so
 * cross-platform downloads are one click away. Neither side auto-downloads;
 * clicks always originate from the user.
 */
export function Download() {
  const t = useT();
  const lp = useLp();
  const lang = useLang();
  const td = t.download;
  // `null` while detection is pending or unavailable (non-Chromium browsers
  // for the arch part). We only use these to decide which section and button
  // get the primary style — neither build downloads automatically.
  // `detectedArch` is reused for both Mac (arm64 vs Intel) and Windows
  // (arm64 vs x64) — `detectMacArch` reads the generic `userAgentData`
  // architecture hint, which is OS-agnostic.
  const [os, setOs] = useState<DetectedOS | null>(null);
  const [detectedArch, setDetectedArch] = useState<MacArch | null>(null);
  const [detectionDone, setDetectionDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setOs(detectOS());
    detectMacArch()
      .then((arch) => {
        if (cancelled) return;
        setDetectedArch(arch);
        setDetectionDone(true);
      })
      .catch(() => {
        if (!cancelled) setDetectionDone(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Recommendation copy — picks the sub-heading based on what we detected.
  let autoCopy: string = td.autoUnknown;
  if (os === "windows") {
    if (detectionDone && detectedArch === "arm64") autoCopy = td.autoWindowsArm64;
    else autoCopy = td.autoWindows;
  } else if (os === "mac" || os === null) {
    if (!detectionDone) autoCopy = td.autoUnknown;
    else if (detectedArch === "arm64") autoCopy = td.autoArm64;
    else if (detectedArch === "x64") autoCopy = td.autoX64;
  }

  // The OS section shown first matches what we detected; the other block
  // follows so users on an unusual setup (wrong UA, work laptop, etc.) can
  // still grab the right build in one click.
  const showWindowsFirst = os === "windows";

  // Each platform lives in its own bordered card so the Mac / Windows / Linux
  // split is visually unambiguous. The detected platform's card also surfaces
  // a small "detected" pill so users know why a button is emphasised.
  // The install note is nested inside the mac card so install guidance stays
  // visually anchored to the buttons it applies to.
  const macSection = (
    <PlatformCard label={td.macPickerLabel} detected={os === "mac"} detectedLabel={td.detectedBadge}>
      <div className="grid gap-3 sm:grid-cols-2">
        <BuildButton
          href={downloadUrl("arm64")}
          label={td.archArm64}
          hint={td.archArm64Hint}
          primary={os !== "windows" && detectedArch !== "x64"}
          os="mac"
          build="mac-arm64"
          lang={lang}
        />
        <BuildButton
          href={downloadUrl("x64")}
          label={td.archX64}
          hint={td.archX64Hint}
          primary={os !== "windows" && detectedArch === "x64"}
          os="mac"
          build="mac-x64"
          lang={lang}
        />
      </div>
      <div className="mt-4 rounded-lg border border-margin dark:border-paper/10 bg-ink/[0.02] dark:bg-paper/[0.03] p-4">
        <div className="font-mono text-[10px] uppercase tracking-wider text-ink/50 dark:text-paper/50">
          {td.gatekeeperLabel}
        </div>
        <p className="mt-2 text-sm text-ink/75 dark:text-paper/75 leading-relaxed">
          {td.gatekeeperExplain}
        </p>
      </div>
    </PlatformCard>
  );

  // Windows: x64 is still the dominant configuration, so without a clear
  // ARM64 signal we recommend the x64 installer. We only flip to ARM64 when
  // we actually detect it (Chromium on Windows on ARM exposes architecture
  // = "arm" via userAgentData).
  const winArm64Detected = os === "windows" && detectionDone && detectedArch === "arm64";
  const winSection = (
    <PlatformCard label={td.winPickerLabel} detected={os === "windows"} detectedLabel={td.detectedBadge}>
      <div className="grid gap-3 sm:grid-cols-2">
        <BuildButton
          href={downloadUrlForBuild("win-x64-setup")}
          label={td.winSetup}
          hint={td.winSetupHint}
          primary={os === "windows" && !winArm64Detected}
          os="windows"
          build="win-x64-setup"
          lang={lang}
        />
        <BuildButton
          href={downloadUrlForBuild("win-x64-portable")}
          label={td.winPortable}
          hint={td.winPortableHint}
          primary={false}
          os="windows"
          build="win-x64-portable"
          lang={lang}
        />
        <BuildButton
          href={downloadUrlForBuild("win-arm64-setup")}
          label={td.winSetupArm64}
          hint={td.winSetupArm64Hint}
          primary={winArm64Detected}
          os="windows"
          build="win-arm64-setup"
          lang={lang}
        />
        <BuildButton
          href={downloadUrlForBuild("win-arm64-portable")}
          label={td.winPortableArm64}
          hint={td.winPortableArm64Hint}
          primary={false}
          os="windows"
          build="win-arm64-portable"
          lang={lang}
        />
      </div>
      <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/[0.08] p-4 dark:border-amber-300/20 dark:bg-amber-300/[0.08]">
        <div className="font-mono text-[10px] uppercase tracking-wider text-amber-900/70 dark:text-amber-100/70">
          {td.winSmartScreenLabel}
        </div>
        <p className="mt-2 text-sm text-ink/75 dark:text-paper/75 leading-relaxed">
          {td.winSmartScreenExplain}
        </p>
        <ol className="mt-3 space-y-2">
          {td.winSmartScreenSteps.map((step, index) => (
            <li key={step} className="flex gap-3 text-sm text-ink/75 dark:text-paper/75">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-600 text-[11px] font-semibold text-white dark:bg-amber-300 dark:text-ink">
                {index + 1}
              </span>
              <span className="leading-relaxed">{step}</span>
            </li>
          ))}
        </ol>
      </div>
    </PlatformCard>
  );

  // Dashed border + muted styling signals "not yet shippable" without a
  // disabled button the user might still try to click.
  const linuxSection = (
    <div className="mt-6 rounded-xl border border-dashed border-margin dark:border-paper/10 p-5 text-left">
      <div className="flex items-center justify-between gap-3 mb-2">
        <span className="font-mono text-[10px] uppercase tracking-wider text-ink/50 dark:text-paper/50">
          {td.linuxPickerLabel}
        </span>
        <span className="inline-flex items-center rounded-full bg-ink/5 dark:bg-paper/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-ink/60 dark:text-paper/60">
          {td.comingSoonBadge}
        </span>
      </div>
      <p className="text-sm text-ink/60 dark:text-paper/60 leading-relaxed">
        {td.linuxComingSoon}
      </p>
    </div>
  );

  return (
    <AuthShell>
      <SeoForPage page="downloads" path="/downloads" />
      <div className="text-center">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-graphite/10 text-graphite dark:bg-paper/10 dark:text-paper">
          <svg
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-6 w-6"
            aria-hidden
          >
            <path d="M10 3v10" />
            <path d="M5 9l5 5 5-5" />
            <path d="M4 17h12" />
          </svg>
        </div>

        <p className="mt-6 font-mono text-[10px] uppercase tracking-wider text-ink/50 dark:text-paper/50">
          {td.eyebrow}
        </p>
        <h1 className="mt-3 font-display text-3xl font-semibold tracking-tight text-ink dark:text-paper">
          {td.heading}
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-ink/65 dark:text-paper/65">
          {autoCopy}
        </p>

        <section aria-labelledby="supported-platforms" className="mt-7 text-left">
          <SectionHeader
            id="supported-platforms"
            kicker={td.supportedKicker}
            heading={td.supportedHeading}
            body={td.supportedBody}
          />
          {showWindowsFirst ? (
            <>
              {winSection}
              {macSection}
            </>
          ) : (
            <>
              {macSection}
              {winSection}
            </>
          )}
          {linuxSection}
        </section>

        <section aria-labelledby="first-run" className="mt-7 text-left">
          <InfoPanel id="first-run" eyebrow={td.firstRunKicker} heading={td.firstRunHeading}>
            <p>{td.firstRunBody}</p>
            <ul className="mt-4 space-y-2">
              {td.firstRunItems.map((item) => (
                <li key={item} className="flex gap-2">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-graphite dark:bg-paper/70" aria-hidden />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </InfoPanel>
        </section>

        <section aria-labelledby="privacy-security" className="mt-5 text-left">
          <InfoPanel id="privacy-security" eyebrow={td.securityKicker} heading={td.securityHeading}>
            <p>{td.securityBody}</p>
            <ul className="mt-4 space-y-2">
              {td.securityItems.map((item) => (
                <li key={item} className="flex gap-2">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-graphite dark:bg-paper/70" aria-hidden />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </InfoPanel>
        </section>

        <div className="mt-8 flex justify-center">
          <Link
            to={lp + "/"}
            className="inline-flex items-center justify-center rounded-full border border-ink/20 dark:border-paper/30 px-6 py-3 text-sm font-medium text-ink dark:text-paper hover:bg-ink/5 dark:hover:bg-paper/5 transition-colors"
          >
            {td.homeCta}
          </Link>
        </div>

        <p className="mt-8 text-xs text-ink/50 dark:text-paper/50">
          {td.footnote}
        </p>
      </div>
    </AuthShell>
  );
}

function SectionHeader({
  id,
  kicker,
  heading,
  body,
}: {
  id: string;
  kicker: string;
  heading: string;
  body: string;
}) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-wider text-ink/50 dark:text-paper/50">
        {kicker}
      </p>
      <h2 id={id} className="mt-2 font-display text-xl font-semibold tracking-tight text-ink dark:text-paper">
        {heading}
      </h2>
      <p className="mt-2 text-sm leading-relaxed text-ink/65 dark:text-paper/65">
        {body}
      </p>
    </div>
  );
}

function InfoPanel({
  id,
  eyebrow,
  heading,
  children,
}: {
  id: string;
  eyebrow: string;
  heading: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-margin dark:border-paper/10 bg-ink/[0.02] dark:bg-paper/[0.03] p-5 text-sm leading-relaxed text-ink/70 dark:text-paper/70">
      <p className="font-mono text-[10px] uppercase tracking-wider text-ink/50 dark:text-paper/50">
        {eyebrow}
      </p>
      <h2 id={id} className="mt-2 font-display text-lg font-semibold tracking-tight text-ink dark:text-paper">
        {heading}
      </h2>
      <div className="mt-3">{children}</div>
    </div>
  );
}

/** Bordered card wrapping one platform's download buttons. */
function PlatformCard({
  label,
  detected,
  detectedLabel,
  children,
}: {
  label: string;
  detected: boolean;
  detectedLabel: string;
  children: ReactNode;
}) {
  return (
    <div className="mt-6 rounded-xl border border-margin dark:border-paper/10 p-5 text-left">
      <div className="flex items-center justify-between gap-3 mb-4">
        <span className="font-mono text-[10px] uppercase tracking-wider text-ink/50 dark:text-paper/50">
          {label}
        </span>
        {detected && (
          <span className="inline-flex items-center rounded-full bg-graphite/10 dark:bg-paper/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-graphite dark:text-paper/80">
            {detectedLabel}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

/** Single download button. Primary variant is filled; secondary is outlined. */
function BuildButton({
  href,
  label,
  hint,
  primary,
  os,
  build,
  lang,
}: {
  href: string;
  label: string;
  hint: string;
  primary: boolean;
  os: string;
  build: string;
  lang: string;
}) {
  const primaryClass =
    "bg-graphite text-paper hover:bg-graphite-muted";
  const secondaryClass =
    "border border-ink/20 dark:border-paper/30 text-ink dark:text-paper hover:bg-ink/5 dark:hover:bg-paper/5";
  return (
    <a
      href={href}
      onClick={() => {
        trackDownloadClick({ source_page: currentSourcePage(), lang, os });
        trackDesktopDownloadClicked(build);
      }}
      className={`group flex flex-col items-center justify-center gap-1 rounded-xl px-5 py-4 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-graphite/40 dark:focus-visible:ring-paper/40 ${primary ? primaryClass : secondaryClass}`}
    >
      <span>{label}</span>
      <span className={`text-[11px] font-mono ${primary ? "text-paper/70" : "text-ink/50 dark:text-paper/50"}`}>
        {hint}
      </span>
    </a>
  );
}
